import type { Game, GameContext, GameMeta, InputState } from '../types';
import { GameEngine } from './GameEngine';
import { CanvasRenderer } from './CanvasRenderer';
import { playShoot, playExplosion } from '../../audio/sfx';

export class ChronoDefenderGame implements Game {
  readonly meta: GameMeta = {
    id: 'chrono-defender',
    title: 'CHRONO DEFENDER',
    blurb: 'Vector wave defense. Trap invaders in Chrono-Bubbles and pop combo chain cascades.',
    tag: 'ARCADE',
    status: 'playable',
  };

  private ctx!: GameContext;
  private canvas!: HTMLCanvasElement;
  private canvasCtx!: CanvasRenderingContext2D;
  private engine!: GameEngine;
  private renderer!: CanvasRenderer;

  private restartPressed = false;
  private keyListener?: (e: KeyboardEvent) => void;
  private clickListener?: (e: MouseEvent) => void;

  private lastBulletCount = 0;
  private lastBubbleCount = 0;

  async mount(ctx: GameContext): Promise<void> {
    this.ctx = ctx;

    this.canvas = document.createElement('canvas');
    this.canvas.className = 'game-canvas';
    this.canvas.setAttribute('aria-label', 'Chrono Defender game canvas');

    ctx.mount.innerHTML = '';
    ctx.mount.appendChild(this.canvas);

    const canvasCtx = this.canvas.getContext('2d');
    if (!canvasCtx) {
      throw new Error('Failed to get 2D rendering context');
    }
    this.canvasCtx = canvasCtx;

    const rect = ctx.mount.getBoundingClientRect();
    const width = rect.width || 800;
    const height = rect.height || 600;

    this.engine = new GameEngine(width, height);
    this.renderer = new CanvasRenderer();

    // Event listeners for 'R' restart key & canvas clicks
    this.keyListener = (e: KeyboardEvent) => {
      if (e.key === 'r' || e.key === 'R') {
        this.restartPressed = true;
      }
    };

    this.clickListener = () => {
      this.restartPressed = true;
    };

    window.addEventListener('keydown', this.keyListener);
    this.canvas.addEventListener('click', this.clickListener);

    this.resize(width, height);
  }

  update(dt: number, input: InputState): void {
    if (!this.engine) return;

    const bands = this.ctx.getBands();
    const audioEnergy = bands.energy || 0;

    // Combine input state
    const engineInput = {
      left: input.left,
      right: input.right,
      up: input.up,
      down: input.down,
      fire: input.fire,
      firePressed: input.firePressed,
      restartPressed: this.restartPressed,
    };

    this.engine.tick(dt, engineInput, audioEnergy);
    this.restartPressed = false; // Reset single-frame flag

    const state = this.engine.getState();

    // Audio SFX cues
    if (state.bullets.length > this.lastBulletCount) {
      try {
        playShoot();
      } catch {
        // Fallback silently if audio context restricted
      }
    }
    if (state.bubbles.length < this.lastBubbleCount) {
      try {
        playExplosion();
      } catch {
        // Fallback
      }
    }

    this.lastBulletCount = state.bullets.length;
    this.lastBubbleCount = state.bubbles.length;

    // Render Canvas frame
    this.renderer.render(this.canvasCtx, state);
  }

  resize(w: number, h: number): void {
    if (!this.canvas) return;

    const dpr = window.devicePixelRatio || 1;
    const width = Math.max(320, w);
    const height = Math.max(240, h);

    this.canvas.width = width * dpr;
    this.canvas.height = height * dpr;
    this.canvas.style.width = `${width}px`;
    this.canvas.style.height = `${height}px`;

    this.canvasCtx.scale(dpr, dpr);
  }

  getHud() {
    if (!this.engine) {
      return { score: 0, lives: 3, wave: 1 };
    }
    return this.engine.getHud();
  }

  dispose(): void {
    if (this.keyListener) {
      window.removeEventListener('keydown', this.keyListener);
    }
    if (this.clickListener && this.canvas) {
      this.canvas.removeEventListener('click', this.clickListener);
    }
    if (this.canvas && this.canvas.parentElement) {
      this.canvas.parentElement.removeChild(this.canvas);
    }
  }
}
